// oxlint-disable da/hardcoded-urls
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiDacheckoutCorePackages } from '@wix/da-papi-types';
import CorePrice from '../../../CorePrice';
import { HERO_TABLE_ID } from '../../constants';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';
import { formatSaleEndDate } from '../../../utils';
import PackageBenefitsSection from './_partials/PackageBenefitsSection';
import { usePackagePickerOrder, useRecommendedPackageIndex } from '../../../hooks';

import s from './HeroTableDesktop.scss';

interface Props {
  packageBenefits?: PapiDacheckoutCorePackages['packageBenefits'];
  referer: string;
  sale?: PackagePickerStoreSlice['sale'];
  saleEndDate?: string;
}

const HeroTableDesktop: FC<Props> = ({ packageBenefits = [], sale, saleEndDate }) => {
  const { t, i18n } = useTranslation();

  const packagePickerOrder = usePackagePickerOrder();
  const recommendedPackageIndex = useRecommendedPackageIndex();

  const defaultBenefits = packageBenefits?.[0]?.title === '' ? packageBenefits?.[0] : undefined;
  const restBenefits = defaultBenefits ? packageBenefits.slice(1) : packageBenefits;
  const firstBenefitHasTitle =
    !defaultBenefits &&
    restBenefits.length > 0 &&
    restBenefits[0].title &&
    restBenefits[0].title.length > 0;

  const highlightPackage = useMemo(
    () => (
      <div
        className={cn(s['table-frame'], s[`highlight-column-${recommendedPackageIndex + 1}`])}
      ></div>
    ),
    [recommendedPackageIndex]
  );

  return (
    <div
      className={cn(
        s['table-wrapper'],
        s[`columns-${packagePickerOrder.length}`],
        recommendedPackageIndex === -1 && s['hide-table-frame'],
        recommendedPackageIndex > -1 && s[`highlight-column-${recommendedPackageIndex + 1}`]
      )}
      id={HERO_TABLE_ID}
    >
      <div className={s['frame-wrapper']}>
        {highlightPackage}
        <table className={cn(s['hero-table'], !firstBenefitHasTitle && s['hero-top-cta'])}>
          <tbody>
            <tr>
              <th className={s['header-column']}></th>
              {packagePickerOrder.map(coreType => (
                <th key={coreType} className={cn(s['core-price'], s[coreType])}>
                  <CorePrice coreType={coreType} />
                </th>
              ))}
            </tr>
          </tbody>
        </table>
        {defaultBenefits && <PackageBenefitsSection packageBenefit={defaultBenefits} />}
      </div>

      {(restBenefits || []).map((packageBenefit, index) => (
        <PackageBenefitsSection
          key={index}
          className={s['benefit-wrapper']}
          highlightPackage={highlightPackage}
          packageBenefit={packageBenefit}
        />
      ))}

      <div className={s['frame-wrapper']}>
        <div className={s['disclaimer']}>
          {sale?.ppShowLegal &&
            saleEndDate &&
            t('packagePicker.heroTable.saleDisclaimer', {
              endDate: formatSaleEndDate(saleEndDate, i18n.language),
            })}
          {!sale && t('packagePicker.billingCycle.new.pricesAreYearlyDisclaimer')}
          {t('packagePicker.billingCycle.new.finalPriceDisclaimer')}
        </div>
        <div className={s['author']}>
          <a href={'https://www.deviantart.com/erisiar/art/Camping-843924883'}>
            {t('packagePicker.heroTable.authorCredits')}
          </a>
        </div>
      </div>
    </div>
  );
};
HeroTableDesktop.displayName = 'HeroTableDesktop';

export default HeroTableDesktop;
